import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Plus, ChevronLeft, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Import components
import ClassGrid from '../components/classroom/ClassGrid';
import ClassList from '../components/classroom/ClassList';
import ClassForm from '../components/classroom/ClassForm';
import FolderForm from '../components/classroom/folders/FolderForm';
import DeleteFolderConfirmation from '../components/classroom/folders/DeleteFolderConfirmation';
import FolderCard from '../components/classroom/FolderCard';
import { ClassData } from '../components/classroom/types';
import { Folder, FolderWithClasses } from '../components/classroom/folders/types';
import { 
  fetchFoldersWithClassCounts, 
  createFolder, 
  updateFolder, 
  deleteFolder, 
  getClassesByFolder
} from '../components/classroom/folders/folderUtils';
import { 
  BlueprintBasic, 
  getClassBlueprints, 
  attachBlueprintsToClass 
} from '../components/classroom/classBlueprintUtils';
import { 
  ToolBasic, 
  getClassTools, 
  attachToolsToClass 
} from '../components/classroom/classToolUtils';

export default function Classroom() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Class-related state
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [showNewClassModal, setShowNewClassModal] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedBlueprints, setSelectedBlueprints] = useState<string[]>([]);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [editingClass, setEditingClass] = useState<ClassData | null>(null);
  const [classBlueprints, setClassBlueprints] = useState<Record<string, BlueprintBasic[]>>({});
  const [, setClassTools] = useState<Record<string, ToolBasic[]>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [newClass, setNewClass] = useState<Partial<ClassData>>({
    title: '',
    description: '',
    video_url: '',
    thumbnail: '',
  });
  
  // Folder-related state (using categories table)
  const [folders, setFolders] = useState<FolderWithClasses[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [currentFolder, setCurrentFolder] = useState<Folder | null>(null);
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<Folder | null>(null);
  const [classesLoading, setClassesLoading] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [copiedClassId, setCopiedClassId] = useState<string | null>(null);

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        
        // Check if user is admin
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('is_admin')
            .eq('id', user.id)
            .single();
          
          setIsAdmin(profile?.is_admin || false);
        }

        // Fetch all data in parallel
        await Promise.all([fetchFolders(), fetchClasses()]);
        setError(null);
      } catch (err) {
        console.error('Error initializing data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // Sync selectedFolder with ?folder= query param (read from URL)
  useEffect(() => {
    const folderParam = searchParams.get('folder');
    if (folderParam) {
      if (selectedFolder !== folderParam) {
        setSelectedFolder(folderParam);
      }
    } else if (selectedFolder !== null) {
      setSelectedFolder(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  
  // Update the classes when folder selection changes
  useEffect(() => {
    if (selectedFolder !== null) {
      const folder = folders.find(f => f.id === selectedFolder);
      setCurrentFolder(folder || null);
      fetchClassesForFolder(selectedFolder);
    } else {
      setCurrentFolder(null);
      fetchClasses();
    }
  }, [selectedFolder, folders]);

  // Write selectedFolder into ?folder= query param (update URL)
  useEffect(() => {
    const current = searchParams.get('folder');
    if (selectedFolder && current !== selectedFolder) {
      const sp = new URLSearchParams(searchParams);
      sp.set('folder', selectedFolder);
      setSearchParams(sp, { replace: false });
    } else if (!selectedFolder && current) {
      const sp = new URLSearchParams(searchParams);
      sp.delete('folder');
      setSearchParams(sp, { replace: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder]);
  
  // Fetch blueprints for classes when needed
  useEffect(() => {
    const fetchBlueprintsForClasses = async () => {
      // Only fetch for classes that don't already have blueprints in state
      const classesNeedingBlueprints = classes.filter(c => !classBlueprints[c.id]);
      
      if (classesNeedingBlueprints.length === 0) return;
      
      try {
        for (const class_ of classesNeedingBlueprints) {
          const blueprints = await getClassBlueprints(class_.id);
          if (blueprints.length > 0) {
            setClassBlueprints(prev => ({
              ...prev,
              [class_.id]: blueprints
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching blueprints for classes:', error);
      }
    };
    
    if (isAdmin && classes.length > 0) {
      fetchBlueprintsForClasses();
    }
  }, [classes, isAdmin, classBlueprints]);
  // Class details rendering moved to /classroom/:classId (ClassDetails)

  
  const fetchFolders = async () => {
    try {
      const foldersWithCounts = await fetchFoldersWithClassCounts();
      setFolders(foldersWithCounts);
    } catch (err) {
      console.error('Error fetching folders:', err);
      setError('Failed to load folders');
    }
  };
  
  const fetchClassesForFolder = async (folderId: string | null) => {
    try {
      setClassesLoading(true);
      const classesInFolder = await getClassesByFolder(folderId);
      setClasses(classesInFolder);
    } catch (err) {
      console.error('Error fetching classes for folder:', err);
      setError('Failed to load classes for this folder');
    } finally {
      setClassesLoading(false);
    }
  };
  
  const handleAddFolder = async (name: string, imgUrl: string, priority: number) => {
    try {
      const newFolder = await createFolder(name, imgUrl, priority);
      await fetchFolders();
      setShowFolderForm(false);
      
      // Automatically navigate to the new folder
      setSelectedFolder(newFolder.id);
    } catch (err) {
      console.error('Error adding folder:', err);
      setError('Failed to add folder');
    }
  };
  
  const handleEditFolder = async (name: string, imgUrl: string, priority: number) => {
    try {
      if (!editingFolder) return;
      
      console.log('Editing folder with data:', { 
        id: editingFolder.id, 
        name, 
        imgUrl, 
        priority 
      });
      
      const updatedFolder = await updateFolder(editingFolder.id, name, imgUrl, priority);
      console.log('Folder update result:', updatedFolder);
      
      await fetchFolders();
      setShowFolderForm(false);
      setEditingFolder(null);
      
      // If we're currently viewing this folder, refresh its name
      if (selectedFolder === editingFolder.id) {
        const folder = folders.find(f => f.id === selectedFolder);
        setCurrentFolder(folder || null);
      }
    } catch (err) {
      console.error('Error updating folder:', err);
      setError('Failed to update folder');
    }
  };
  
  const handleDeleteFolder = async (moveClassesToParent: boolean) => {
    try {
      if (!folderToDelete) return;
      
      await deleteFolder(folderToDelete.id, moveClassesToParent);
      
      // If we're deleting the currently selected folder, reset to all classes
      if (selectedFolder === folderToDelete.id) {
        setSelectedFolder(null);
      }
      
      fetchFolders();
      setShowDeleteFolderModal(false);
      setFolderToDelete(null);
    } catch (err) {
      console.error('Error deleting folder:', err);
      setError('Failed to delete folder');
    }
  };
  


  const fetchClasses = async () => {
    try {
      const { data, error } = await supabase
        .from('classes')
        .select(`
          *,
          class_categories!inner (
            category_id
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const classesWithCategories = data?.map(class_ => ({
        ...class_,
        categories: (class_.class_categories as { category_id: string }[]).map((cc) => cc.category_id)
      })) || [];
      
      setClasses(classesWithCategories);
    } catch (err) {
      console.error('Error fetching classes:', err);
      setError('Failed to load classes');
    }
  };


  const getYouTubeVideoId = (url: string) => {
    if (!url) return null;
    
    try {
      const urlObj = new URL(url);
      let videoId = '';
      
      if (urlObj.hostname.includes('youtube.com')) {
        videoId = urlObj.searchParams.get('v') || '';
      } else if (urlObj.hostname === 'youtu.be') {
        videoId = urlObj.pathname.slice(1);
      }
      
      return videoId.match(/^[a-zA-Z0-9_-]{11}$/) ? videoId : null;
    } catch {
      return null;
    }
  };

  const handleAddClass = async () => {
    try {
      if (!newClass.title || !newClass.description || !newClass.video_url || selectedCategories.length === 0) {
        return;
      }

      // Extract YouTube video ID for thumbnail generation
      const videoId = getYouTubeVideoId(newClass.video_url as string);
      let thumbnailUrl = '';
      
      if (videoId) {
        thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      }

      const { data, error } = await supabase
        .from('classes')
        .insert([
          {
            title: newClass.title,
            description: newClass.description,
            video_url: newClass.video_url,
            thumbnail: thumbnailUrl,
          },
        ])
        .select();

      if (error) throw error;

      if (data && data.length > 0) {
        // Insert category associations
        const classId = data[0].id;
        const categoryAssociations = selectedCategories.map(categoryId => ({
          class_id: classId,
          category_id: categoryId,
        }));

        const { error: categoryError } = await supabase
          .from('class_categories')
          .insert(categoryAssociations);

        if (categoryError) throw categoryError;
        
        // If admin and blueprints selected, attach them to the class
        if (isAdmin && selectedBlueprints.length > 0) {
          await attachBlueprintsToClass(classId, selectedBlueprints);
        }

        // If admin and tools selected, attach them to the class
        if (isAdmin && selectedTools.length > 0) {
          await attachToolsToClass(classId, selectedTools);
        }

        // Update the classes state
        const newClassWithCategories = {
          ...data[0],
          categories: selectedCategories,
        };

        setClasses([...classes, newClassWithCategories]);
        
        // Reset form state
        setNewClass({
          title: '',
          description: '',
          video_url: '',
          thumbnail: '',
        });
        setSelectedCategories([]);
        setSelectedBlueprints([]);
        setSelectedTools([]);
        setShowNewClassModal(false);
      }
    } catch (error) {
      console.error('Error adding class:', error);
    }
  };

  const handleEditClass = async (updatedClass: ClassData, updatedCategories: string[], updatedBlueprints: string[] = [], updatedTools: string[] = []) => {
    try {
      // Get the YouTube video ID for thumbnail generation if video URL has changed
      let thumbnailUrl = updatedClass.thumbnail;
      if (updatedClass.video_url !== editingClass?.video_url) {
        const videoId = getYouTubeVideoId(updatedClass.video_url);
        if (videoId) {
          thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        }
      }

      // Update the class data
      const { error } = await supabase
        .from('classes')
        .update({
          title: updatedClass.title,
          description: updatedClass.description,
          video_url: updatedClass.video_url,
          thumbnail: thumbnailUrl,
        })
        .eq('id', updatedClass.id);

      if (error) throw error;

      // Delete existing category associations
      const { error: deleteError } = await supabase
        .from('class_categories')
        .delete()
        .eq('class_id', updatedClass.id);

      if (deleteError) throw deleteError;

      // Create new category associations
      const categoryAssociations = updatedCategories.map(categoryId => ({
        class_id: updatedClass.id,
        category_id: categoryId,
      }));

      if (categoryAssociations.length > 0) {
        const { error: insertError } = await supabase
          .from('class_categories')
          .insert(categoryAssociations);

        if (insertError) throw insertError;
      }
      
      // If admin, update blueprint associations
      if (isAdmin) {
        await attachBlueprintsToClass(updatedClass.id, updatedBlueprints);
        
        // Update the class blueprints in the state
        if (updatedBlueprints.length > 0) {
          // We'll fetch the updated blueprints to get their full details
          const blueprints = await getClassBlueprints(updatedClass.id);
          setClassBlueprints(prev => ({
            ...prev,
            [updatedClass.id]: blueprints
          }));
        } else {
          // If no blueprints, remove from state
          setClassBlueprints(prev => {
            const newState = { ...prev };
            delete newState[updatedClass.id];
            return newState;
          });
        }

        // Update tool associations
        await attachToolsToClass(updatedClass.id, updatedTools);
        
        // Update the class tools in the state
        if (updatedTools.length > 0) {
          // We'll fetch the updated tools to get their full details
          const tools = await getClassTools(updatedClass.id);
          setClassTools(prev => ({
            ...prev,
            [updatedClass.id]: tools
          }));
        } else {
          // If no tools, remove from state
          setClassTools(prev => {
            const newState = { ...prev };
            delete newState[updatedClass.id];
            return newState;
          });
        }
      }

      // Update the classes state
      const updatedClassWithCategories = {
        ...updatedClass,
        categories: updatedCategories,
        thumbnail: thumbnailUrl,
      };

      setClasses(classes.map(c => c.id === updatedClass.id ? updatedClassWithCategories : c));
      
      // Reset editing state
      setEditingClass(null);
      setSelectedCategories([]);
      setSelectedBlueprints([]);
      setSelectedTools([]);
      setShowNewClassModal(false);
    } catch (error) {
      console.error('Error updating class:', error);
    }
  };

  const getFilteredClasses = () => {
    let filtered = classes;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(c => c.title.toLowerCase().includes(query));
    }
    return filtered;
  };

  const copyClassLink = async (classId: string) => {
    const url = `${window.location.origin}/classroom/${classId}${selectedFolder ? `?folder=${selectedFolder}` : ''}`;
    await navigator.clipboard.writeText(url);
    setCopiedClassId(classId);
    setTimeout(() => setCopiedClassId(null), 2000);
  };

  if (loading) {
    return (
      <div className="p-8 min-h-screen">
        <h1 className="text-[20px] leading-[28px] font-bold text-white mb-8 tracking-tight">Classroom</h1>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-20 h-10 bg-[#1a2533] rounded-xl opacity-40 animate-pulse" />
          <div className="w-28 h-10 bg-[#1a2533] rounded-xl opacity-40 animate-pulse" />
          <div className="flex-grow" />
          <div className="w-12 h-10 bg-[#1a2533] rounded-xl opacity-40 animate-pulse" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
            <div key={index} className="bg-gradient-to-br from-[#141C28] to-[#0E1621] rounded-xl p-5 animate-pulse">
              <div className="w-16 h-16 bg-[#1a2533] rounded-lg mb-3 mx-auto" />
              <div className="h-5 bg-[#1a2533] rounded-full w-3/4 mx-auto mb-2" />
              <div className="h-4 bg-[#1a2533] rounded-full w-1/4 mx-auto" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white py-4 px-2 sm:p-8 w-full max-w-full">
      <motion.h1 
        className="text-[20px] leading-[28px] font-bold text-white mb-8 tracking-tight"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        Classroom
      </motion.h1>
      
      {/* Folder Navigation */}
      {selectedFolder && currentFolder && (
        <motion.div 
          className="mb-8 flex items-center gap-4"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.button
            onClick={() => {
              // Go back to root (no parent-child folder relationship)
              setSelectedFolder(null);
            }}
            className="flex items-center gap-2 text-white bg-[#1a2533] px-5 py-2.5 rounded-xl hover:bg-[#232f3e] transition-colors shadow-lg" 
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </motion.button>
          
          <div className="text-[20px] leading-[28px] font-semibold text-white tracking-tight">{currentFolder.name}</div>
        </motion.div>
      )}
      
      {/* Folder Actions & Search (for admin) */}
      <motion.div 
        className="mb-8 flex items-center gap-3 flex-wrap"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        {/* Search box */}
        <div className="relative mr-auto">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <Search size={18} />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="bg-[#1a2533] text-white rounded-xl px-10 py-2.5 w-64 focus:outline-none focus:ring-2 focus:ring-[#c9fffc] shadow-lg text-[14px] tracking-tight"
          />
        </div>

        {isAdmin && (
          <motion.button
            onClick={() => {
              setEditingFolder(null);
              setShowFolderForm(true);
            }}
            className="flex items-center gap-2 text-white bg-[#1c64f2] px-5 py-2.5 rounded-xl hover:bg-blue-600 transition-colors shadow-lg"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <Plus className="w-5 h-5" />
            Add Folder
          </motion.button>
        )}
      </motion.div>
      
      {error && (
        <motion.div 
          className="mb-8 p-4 bg-red-900/30 border border-red-500/30 text-white rounded-xl backdrop-blur-sm shadow-lg"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {error}
        </motion.div>
      )}
      
      {/* Show folders or classes */}
      {!selectedFolder ? (
        /* Show Folders */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {folders.map((folder, index) => (
            <FolderCard
              key={folder.id}
              folder={folder}
              index={index}
              isAdmin={isAdmin}
              onClick={async () => {
                try {
                  // Fetch classes in this folder and open the first one directly
                  const list = await getClassesByFolder(folder.id);
                  if (list && list.length > 0) {
                    const first = list[0];
                    navigate(`/classroom/${first.id}?folder=${folder.id}`);
                  } else {
                    // If no classes found, fall back to showing the folder view
                    setSelectedFolder(folder.id);
                  }
                } catch (e) {
                  console.warn('Failed to open first class, falling back to folder view:', e);
                  setSelectedFolder(folder.id);
                }
              }}
              onEdit={(e) => {
                e.stopPropagation();
                setEditingFolder(folder);
                setShowFolderForm(true);
              }}
              onDelete={(e) => {
                e.stopPropagation();
                setFolderToDelete(folder);
                setShowDeleteFolderModal(true);
              }}
            />
          ))}
        </div>
      ) : (
        /* Show Classes in Folder */
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          {classesLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {[1, 2, 3, 4, 5, 6].map((index) => (
                <motion.div 
                  key={index} 
                  className="min-h-screen text-white relative overflow-x-hidden w-full max-w-full px-6 md:px-0"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 0.7, y: 0 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: index * 0.05,
                    ease: [0.33, 1, 0.68, 1]
                  }}
                >
                  <div className="aspect-video bg-[#1a2533] animate-pulse" />
                  <div className="p-6">
                    <div className="h-7 bg-[#1a2533] rounded-full w-3/4 mb-3 animate-pulse" />
                    <div className="h-4 bg-[#1a2533] rounded-full w-full mb-2 animate-pulse" />
                    <div className="h-4 bg-[#1a2533] rounded-full w-5/6 mb-2 animate-pulse" />
                    <div className="h-4 bg-[#1a2533] rounded-full w-4/6 animate-pulse" />
                    <div className="mt-4 flex justify-between items-center">
                      <div className="h-4 bg-[#1a2533] rounded-full w-20 animate-pulse" />
                      <div className="flex gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#1a2533] animate-pulse" />
                        <div className="w-8 h-8 rounded-full bg-[#1a2533] animate-pulse" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <AnimatePresence>
              {currentFolder ? (
                // Use list view when inside a folder
                <ClassList 
                  classes={getFilteredClasses()}
                  categories={folders}
                  isAdmin={isAdmin}
                  copiedClassId={copiedClassId}
                  onCopyLink={copyClassLink}
                  onEdit={(class_) => {
                    setEditingClass(class_);
                    setSelectedCategories(class_.categories);
                    setShowNewClassModal(true);
                  }}
                  onAddClass={() => setShowNewClassModal(true)}
                  currentFolderId={selectedFolder}
                />
              ) : (
                // Use grid view for main screen
                <ClassGrid 
                  classes={getFilteredClasses()}
                  categories={folders}
                  isAdmin={isAdmin}
                  copiedClassId={copiedClassId}
                  onCopyLink={copyClassLink}
                  onEdit={(class_) => {
                    setEditingClass(class_);
                    setSelectedCategories(class_.categories);
                    setShowNewClassModal(true);
                  }}
                  onAddClass={() => setShowNewClassModal(true)}
                />
              )}
            </AnimatePresence>
          )}
        </motion.div>
      )}



      {/* New/Edit Class Modal */}
      {showNewClassModal && (
        <ClassForm
          onClose={() => {
            setShowNewClassModal(false);
            setEditingClass(null);
            setSelectedCategories([]);
            setSelectedBlueprints([]);
            setSelectedTools([]);
          }}
          onSubmit={(classData, selectedCats, blueprintIds, toolIds) => {
            if (editingClass) {
              // Pass the updated data directly to handleEditClass instead of relying on state updates
              handleEditClass({
                ...editingClass,
                ...classData
              }, selectedCats, blueprintIds, toolIds);
            } else {
              setNewClass(classData);
              setSelectedCategories(selectedCats);
              setSelectedBlueprints(blueprintIds);
              setSelectedTools(toolIds);
              handleAddClass();
            }
          }}
          categories={folders} // Use folders (categories) for the class form
          editingClass={editingClass}
          isAdmin={isAdmin}
        />
      )}
      
      {/* New/Edit Folder Modal */}
      {showFolderForm && (
        <FolderForm
          onClose={() => {
            setShowFolderForm(false);
            setEditingFolder(null);
          }}
          onSubmit={(name, imgUrl, priority) => {
            if (editingFolder) {
              handleEditFolder(name, imgUrl, priority);
            } else {
              handleAddFolder(name, imgUrl, priority);
            }
          }}
          editingFolder={editingFolder}
          folders={folders}
        />
      )}
      
      {/* Delete Folder Confirmation */}
      {showDeleteFolderModal && folderToDelete && (
        <DeleteFolderConfirmation
          folder={folderToDelete}
          hasClasses={'class_count' in folderToDelete ? (folderToDelete as FolderWithClasses).class_count > 0 : false}
          onCancel={() => {
            setShowDeleteFolderModal(false);
            setFolderToDelete(null);
          }}
          onConfirm={handleDeleteFolder}
        />
      )}
      

    </div>
  );
}
